import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, CircularProgress, Alert, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { MultiSelect } from "primereact/multiselect";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import CommonTable from "../../../common/CommonTable";

const backendUrl = (process.env.REACT_APP_BACKEND_API_URL_LOCAL || "").replace(/\/$/, "");
const REPORTS_URL = `${backendUrl}/common/acp-reports/`;
const HAZARD_MAP_URL = `${backendUrl}/common/acp-hazard-by-admin/`;

// Same 4-color risk palette used site-wide (Dashboard/Home/Forecastmap.js
// ALERT_COLORS, reused verbatim by FireRisk/Forecast.js and
// Lightning/FlashDensity.js) - lowest-count bucket to highest-count bucket.
const BUCKET_COLORS = [
    "rgb(148, 203, 85)",  // lowest
    "rgb(243, 235, 18)",
    "rgb(243, 155, 58)",
    "rgb(230, 45, 58)",   // highest
];
const NO_DATA_COLOR = "rgba(222, 222, 223)";

// admcode matches AcpHazardByAdminView's level selector: 1=Municipality,
// 2=Sub-Municipality, 3=Suco.
const MAP_LEVELS = [
    { key: "municipality", admcode: 1, title: "Municipality", file: "/DATA/OSMbased_adm1_boundary.json", nameKey: "ADM1" },
    { key: "submunicipality", admcode: 2, title: "Sub-Municipality", file: "/DATA/OSMbased_adm2_boundary.json", nameKey: "ADM2" },
    { key: "suco", admcode: 3, title: "Suco", file: "/DATA/OSMbased_adm3_boundary.json", nameKey: "ADM3" },
];

const getBucketColor = (count, min, max) => {
    if (count === undefined || count === null) return NO_DATA_COLOR;
    if (max === min) return BUCKET_COLORS[3];
    const ratio = (count - min) / (max - min);
    const idx = Math.min(3, Math.floor(ratio * 4));
    return BUCKET_COLORS[idx];
};

const HazardMap = ({ title, geojson, dataMap, min, max, nameKey, selectedYears }) => {
    const style = useCallback((feature) => {
        const entry = dataMap.get(feature.properties.ADM_Code);
        return {
            fillColor: getBucketColor(entry?.total, min, max),
            fillOpacity: 1,
            color: "#010101",
            weight: 0.6,
            opacity: 1,
        };
    }, [dataMap, min, max]);

    const onEachFeature = useCallback((feature, layer) => {
        const entry = dataMap.get(feature.properties.ADM_Code);
        const name = feature.properties[nameKey] || feature.properties.ADM_Code;
        const total = entry?.total ?? 0;

        // Whatever years are selected in the filter, zero-fill any that
        // this admin area had no incidents in - e.g. selecting 2026/2025/
        // 2024 always shows all three lines, even if one is 0.
        const yearLines = [...selectedYears]
            .sort((a, b) => Number(b) - Number(a))
            .map((y) => `${y}: ${entry?.byYear?.[Number(y)] ?? 0}`)
            .join("<br/>");

        layer.bindTooltip(
            `<strong>${name}</strong><br/>Total Incidents: ${total}${yearLines ? `<br/>${yearLines}` : ""}`,
            { sticky: true }
        );
    }, [dataMap, nameKey, selectedYears]);

    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#334155", mb: 0.5, textAlign: "center" }}>
                {title}
            </Typography>
            <Box sx={{ height: 320, border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                {geojson ? (
                    <MapContainer center={[-8.8742, 125.7275]} zoom={7.4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <GeoJSON key={`${title}-${min}-${max}-${dataMap.size}-${selectedYears.join(",")}`} data={geojson} style={style} onEachFeature={onEachFeature} />
                    </MapContainer>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// Same PrimeReact MultiSelect (checkbox dropdown) used by Blackspot/Death
// Data's year filters (RoadAccident/DP_Blackspot/DPBlackspot.js,
// DP_DeathData/DatapanelDeathdata.js), for a consistent year-picker model
// across these "pick year(s), view a table" admin pages.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_START = 2020;
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - YEAR_START + 1 }, (_, i) => {
    const y = (CURRENT_YEAR - i).toString();
    return { name: y, value: y };
});

// Same hazard columns as statistics_acp / common ACP_HAZARD_FIELDS - each
// holds the number of families THAT SPECIFIC INCIDENT affected, not an
// event count (see AcpIncidentReportsListView's docstring).
const HAZARD_COLUMNS = [
    ["wind_damage", "Wind Damage"],
    ["fire", "Fire"],
    ["flood", "Flood"],
    ["landslide", "Landslide"],
    ["storm_surge", "Storm Surge"],
    ["social_conflict", "Social Conflict"],
    ["other_hazard", "Other Hazard"],
    ["road_accident", "Road Accident"],
];

const IMPACT_COLUMNS = [
    ["dead", "Dead"],
    ["injured", "Injured"],
    ["missing", "Missing"],
    ["affected_families", "Affected Families"],
    ["total_family_members", "Total Family Members"],
    ["evacuated_people", "Evacuated People"],
    ["damaged_road", "Damaged Road"],
    ["bridges", "Bridges"],
    ["damaged_houses", "Damaged Houses"],
    ["public_buildings", "Public Buildings"],
];

// AcpIncidentReportsListView already resolves suco_code against the
// Municipality master list server-side (LEFT JOIN on adm3_code), so every
// row here carries adm1/adm2/adm3 names directly - no client-side lookup
// needed, and no bare suco_code shown to the user.
const AcpDataReport = () => {
    const [years, setYears] = useState([CURRENT_YEAR.toString()]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedHazard, setSelectedHazard] = useState(HAZARD_COLUMNS[0][0]);
    const [boundaries, setBoundaries] = useState({ municipality: null, submunicipality: null, suco: null });
    const [hazardMapData, setHazardMapData] = useState({
        municipality: { dataMap: new Map(), min: 0, max: 0 },
        submunicipality: { dataMap: new Map(), min: 0, max: 0 },
        suco: { dataMap: new Map(), min: 0, max: 0 },
    });
    const [mapsLoading, setMapsLoading] = useState(false);
    const [mapsError, setMapsError] = useState(null);

    const fetchRows = useCallback(async (selectedYears) => {
        if (!selectedYears.length) {
            setRows([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${REPORTS_URL}?years=${selectedYears.join(",")}`);
            if (!res.ok) throw new Error("Failed to load ACP data.");
            const data = await res.json();
            setRows(Array.isArray(data.rows) ? data.rows : []);
        } catch (e) {
            setError(e.message || "Failed to load ACP data.");
            setRows([]);
        }
        setLoading(false);
    }, []);

    const fetchHazardMaps = useCallback(async (hazardField, selectedYears) => {
        setMapsLoading(true);
        setMapsError(null);
        try {
            const yearsQuery = selectedYears.length ? `&years=${selectedYears.join(",")}` : "";
            const [muniRes, subMuniRes, sucoRes] = await Promise.all(
                MAP_LEVELS.map(({ admcode }) =>
                    fetch(`${HAZARD_MAP_URL}?admcode=${admcode}&hazard=${hazardField}${yearsQuery}`).then((r) => {
                        if (!r.ok) throw new Error("Failed to load hazard map data.");
                        return r.json();
                    })
                )
            );

            const buildLevel = (payload) => {
                const dataMap = new Map();
                (payload.data || []).forEach((row) => {
                    if (row.code === null || row.code === undefined) return;
                    const entry = dataMap.get(row.code) || { total: 0, byYear: {} };
                    entry.total += row.count;
                    entry.byYear[row.year] = (entry.byYear[row.year] || 0) + row.count;
                    dataMap.set(row.code, entry);
                });
                const totals = Array.from(dataMap.values(), (v) => v.total);
                return {
                    dataMap,
                    min: totals.length ? Math.min(...totals) : 0,
                    max: totals.length ? Math.max(...totals) : 0,
                };
            };

            setHazardMapData({
                municipality: buildLevel(muniRes),
                submunicipality: buildLevel(subMuniRes),
                suco: buildLevel(sucoRes),
            });
        } catch (e) {
            setMapsError(e.message || "Failed to load hazard map data.");
        }
        setMapsLoading(false);
    }, []);

    useEffect(() => {
        fetchRows(years);
        fetchHazardMaps(selectedHazard, years);

        MAP_LEVELS.forEach(({ key, file }) => {
            fetch(file)
                .then((r) => r.json())
                .then((geojson) => setBoundaries((prev) => ({ ...prev, [key]: geojson })))
                .catch(() => {});
        });
        // Only on mount - subsequent loads are driven by the year/hazard
        // filters' own onChange handlers, which already have the freshly
        // selected value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleYearsChange = (e) => {
        const next = [...e.value].sort();
        setYears(next);
        fetchRows(next);
        fetchHazardMaps(selectedHazard, next);
    };

    const handleHazardChange = (e) => {
        const next = e.target.value;
        setSelectedHazard(next);
        fetchHazardMaps(next, years);
    };

    const columns = useMemo(() => [
        { key: "incident_date", label: "Date", sortable: true, align: "left" },
        { key: "adm1", label: "Municipality", sortable: true, align: "left" },
        { key: "adm2", label: "Sub-Municipality", sortable: true, align: "left" },
        { key: "adm3", label: "Suco", sortable: true, align: "left" },
        { key: "location", label: "Location", sortable: false, align: "left" },
        ...HAZARD_COLUMNS.map(([key, label]) => ({ key, label, sortable: true, align: "center" })),
        ...IMPACT_COLUMNS.map(([key, label]) => ({ key, label, sortable: true, align: "center" })),
        { key: "remarks", label: "Remarks", sortable: false, align: "left" },
    ], []);

    return (
        <Box>
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e2e8f0", borderRadius: "12px", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>Year</Typography>
                    <MultiSelect
                        value={years}
                        onChange={handleYearsChange}
                        options={YEAR_OPTIONS}
                        optionLabel="name"
                        placeholder="Select Year(s)"
                        display="chip"
                        className="w-full md:w-20rem"
                        style={{ width: "400px", fontSize: "13px" }}
                    />
                </Box>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                    {loading ? "Loading…" : `${rows.length.toLocaleString()} incident${rows.length === 1 ? "" : "s"}`}
                </Typography>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e2e8f0", borderRadius: "12px" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                        Hazard Distribution
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="acp-hazard-select-label">Hazard</InputLabel>
                        <Select
                            labelId="acp-hazard-select-label"
                            label="Hazard"
                            value={selectedHazard}
                            onChange={handleHazardChange}
                        >
                            {HAZARD_COLUMNS.map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {mapsError && <Alert severity="error" sx={{ mb: 2 }}>{mapsError}</Alert>}

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", position: "relative" }}>
                    {mapsLoading && (
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, background: "rgba(255,255,255,0.6)" }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {MAP_LEVELS.map(({ key, title, nameKey }) => (
                        <HazardMap
                            key={key}
                            title={title}
                            nameKey={nameKey}
                            geojson={boundaries[key]}
                            dataMap={hazardMapData[key].dataMap}
                            min={hazardMapData[key].min}
                            max={hazardMapData[key].max}
                            selectedYears={years}
                        />
                    ))}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, mt: 2, flexWrap: "wrap" }}>
                    {["Lowest", "Low-Mid", "Mid-High", "Highest"].map((label, idx) => (
                        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 14, height: 14, borderRadius: "3px", backgroundColor: BUCKET_COLORS[idx] }} />
                            <Typography variant="caption" sx={{ color: "#475569" }}>{label}</Typography>
                        </Box>
                    ))}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: "3px", backgroundColor: NO_DATA_COLOR }} />
                        <Typography variant="caption" sx={{ color: "#475569" }}>No Data</Typography>
                    </Box>
                </Box>
            </Paper>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <CommonTable
                    tableData={rows}
                    columns={columns}
                    tableName="ACP_Incident_Reports"
                    searchableKeys={["adm1", "adm2", "adm3", "location", "remarks"]}
                    searchPlaceholder="Search by municipality, suco, location..."
                    emptyMessage={years.length === 0 ? "Select at least one year to view data." : "No ACP incidents found for the selected year(s)."}
                />
            )}
        </Box>
    );
};

export default AcpDataReport;
